var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo } from 'react';
import { FormProvider, useController, useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { CreateDareFormSchema } from 'root/src/client/web/record/CreatorData/CreatorDare/CreateCreatorDare';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Field } from 'root/src/client/v2/common/components/ui/field';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { Textarea } from 'root/src/client/v2/common/components/ui/textarea';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
const TitleField = () => {
    var _a;
    const form = useFormContext();
    const { field, fieldState } = useController({ name: 'title', control: form.control });
    return (<Field label="Title" htmlFor="cd-title" error={(_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message}>
			<Input id="cd-title" maxLength={50} placeholder="Short and catchy" error={Boolean(fieldState.error)} {...field}/>
		</Field>);
};
const DescriptionField = () => {
    var _a;
    const form = useFormContext();
    const { field, fieldState } = useController({ name: 'description', control: form.control });
    return (<Field label="Description" htmlFor="cd-desc" error={(_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message}>
			<Textarea id="cd-desc" rows={3} maxLength={200} placeholder="What should the creator do?" error={Boolean(fieldState.error)} {...field}/>
		</Field>);
};
export const RedesignCreateDareModal = memo(({ dropId, onCreate }) => {
    const modal = useRedesignModal();
    const { loading, success, error, run } = useAsyncAction();
    const form = useForm({
        resolver: zodResolver(CreateDareFormSchema),
        defaultValues: { title: '', description: '' },
    });
    const createMutation = trpc.dares.createEventFulfilledCreatorDare.useMutation();
    const onSubmit = form.handleSubmit((data) => {
        void run(() => __awaiter(void 0, void 0, void 0, function* () {
            yield createMutation.mutateAsync({ dropId, title: data.title, description: data.description });
            yield onCreate();
        }), {
            onSuccess: modal.hide,
            toast: {
                loading: 'Creating dare...',
                success: 'Dare created and is pending review.',
                error: caught => (caught instanceof Error ? caught.message : 'Could not create dare'),
            },
        });
    });
    return (<RedesignModalShell className="sm:max-w-md">
			<div className="flex w-full flex-col items-center gap-1 text-center">
				<h2 className="font-display text-xl font-bold text-foreground">Create your own dare</h2>
				<p className="text-sm text-muted-foreground">Your dare will be submitted for review and becomes available once approved.</p>
			</div>

			<FormProvider {...form}>
				<form onSubmit={onSubmit} className="flex w-full flex-col gap-5">
					<TitleField />
					<DescriptionField />
					<Button type="submit" variant="brand" loading={loading} success={success} error={error} className="w-full rounded-lg text-sm font-semibold">
						Create Dare
					</Button>
					<p className="text-center text-[11px] text-muted-foreground">All dares must comply with our community guidelines and can be disabled at any time.</p>
				</form>
			</FormProvider>
		</RedesignModalShell>);
});
